export type TtsHandle = {
  /** Stop playback immediately. Safe to call multiple times. */
  stop: () => void;
  /** Resolves when playback ends naturally (not when stop() is called). */
  ended: Promise<void>;
};

export function isNativeTtsAvailable(): boolean {
  return typeof window !== "undefined" && "speechSynthesis" in window;
}

/**
 * Speak `text` out loud through the browser's own `window.speechSynthesis`.
 *
 * The standalone build has no server-side TTS: the Azure Speech fallback that
 * used to sit behind this was removed along with the rest of the cloud
 * dependencies. Browsers without speechSynthesis get a no-op handle that
 * resolves immediately, so callers need no extra branching.
 */
export function speakText(text: string, bcp47Locale: string): TtsHandle {
  if (!isNativeTtsAvailable()) {
    console.info("[Voice] TTS unavailable in this browser; skipping playback");
    return { stop: () => {}, ended: Promise.resolve() };
  }

  try {
    return speakNative(text, bcp47Locale);
  } catch (err) {
    console.warn("[Voice] native TTS threw; skipping playback", err);
    return { stop: () => {}, ended: Promise.resolve() };
  }
}

function speakNative(text: string, bcp47Locale: string): TtsHandle {
  const synth = window.speechSynthesis;
  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = bcp47Locale;

  let endedResolve: () => void = () => {};
  const ended = new Promise<void>((resolve) => {
    endedResolve = resolve;
  });

  utterance.addEventListener("end", () => endedResolve());
  utterance.addEventListener("error", (event) => {
    console.warn("[Voice] native TTS error", event);
    endedResolve();
  });

  // Chrome sometimes leaves a prior queue active; cancel before speak to be
  // deterministic.
  synth.cancel();
  synth.speak(utterance);

  return {
    stop: () => {
      try {
        synth.cancel();
      } catch {
        // ignore
      }
      endedResolve();
    },
    ended,
  };
}
